// Spec iter144 Phase 3 (Feb 2026) — Hovercard riche au survol du bouton
// Info d'une ligne AVI. Affiche : référence, statut, objet, description,
// décideurs/réceptionnaires avec leur statut individuel, témoins.
//
// Le trigger est le bouton Info existant du tableau (icône `Info`).
// La HoverCard s'ouvre après ~250 ms de survol (comportement Radix par défaut).
//
// Notes :
//   • Utilise HoverCard qui est déjà portalé (z-[100]) → pas de coupure
//     par les cellules du tableau ni par le KPI collant.
//   • Sur click du bouton Info, on garde le comportement original :
//     ouvrir la fiche détaillée (modale AVI). La hovercard est un
//     PRÉ-VIEW, pas un remplaçant.

import React from "react";
import { CheckCircle2, XCircle, Clock, Eye, Stamp, Info as InfoIcon } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import { AVI_STATUS_BADGE } from "@/components/avi/AviStatusBadge";
import { AVI_STATUS_OVERRIDES } from "@/lib/aviSubtypePalette";
import UserAvatar from "@/components/ui/UserAvatar";

const DECISION_META = {
  approuvee: { label: "Approuvée", color: "#4ADE80", Icon: CheckCircle2 },
  accusee:   { label: "Reçu",      color: "#F97316", Icon: Stamp },
  lue:       { label: "Lue",       color: "#5aa8e0", Icon: Eye },
  refusee:   { label: "Refusée",   color: "#F87171", Icon: XCircle },
  pending:   { label: "En attente", color: "#F9BC26", Icon: Clock },
  bypassed:  { label: "Bypassée (Admin)", color: "#9CA3AF", Icon: XCircle },
};

function Chip({ user, size = 22 }) {
  return (
    <span className="inline-flex rounded-full shrink-0" style={{ border: "1.5px solid #161616" }}>
      <UserAvatar user={user || { first_name: "?", last_name: "" }} size={size} showPopover={false} />
    </span>
  );
}

export default function AviInfoHovercard({ avi, usersById, children }) {
  if (!avi) return children || null;
  const subtypeMeta = AVI_SUBTYPES[avi.avi_type] || {};
  const baseStatusMeta = AVI_STATUS_BADGE[avi.status] || {};
  // Spec iter164l (2026-02-25) — surcharge palette par sous-type
  // via table centralisée.
  const subOverride = AVI_STATUS_OVERRIDES[avi.avi_type]?.[avi.status];
  const statusMeta = subOverride ? { ...baseStatusMeta, ...subOverride } : baseStatusMeta;
  const StatusIcon = statusMeta.Icon || InfoIcon;
  const participants = avi.participants || [];
  const active = participants.filter((p) => p.role === "decideur" || p.role === "receptionnaire" || p.role === "lecteur");
  const witnesses = participants.filter((p) => p.role === "temoin");
  const activeLabel = avi.avi_type === "accuse" ? "Réceptionnaires"
                    : avi.avi_type === "information" ? "Lecteurs"
                    : "Décideurs";

  const getDecisionMeta = (p) => {
    if (p.decision) return DECISION_META[p.decision] || DECISION_META.pending;
    if (p.decision_bypassed_at) return DECISION_META.bypassed;
    return DECISION_META.pending;
  };

  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        align="end"
        side="left"
        sideOffset={8}
        className="w-[460px] p-0 border-0 bg-transparent shadow-none"
        data-testid={`avi-info-hovercard-${avi.id}`}
      >
        <div
          className="rounded-xl overflow-hidden"
          style={{
            background: "#161616",
            // Spec iter164m (2026-02-25) — Bordure fine sur les 4 côtés
            // + bandeau du haut ÉPAIS (3px) dans la couleur du sous-type
            //   → identité chromatique renforcée dès l'ouverture du
            //     hovercard, cohérente avec le Kanban et les KPI.
            border: `1px solid ${subtypeMeta.color || "#2A2A2A"}`,
            borderTop: `3px solid ${subtypeMeta.color || "#2A2A2A"}`,
            boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
          }}
        >
          {/* Header : sous-type + ref + statut */}
          <div
            className="flex items-center justify-between px-3.5 py-2.5"
            style={{ background: "#1B1B1B", borderBottom: `1px solid ${subtypeMeta.color || "#2A2A2A"}44` }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {subtypeMeta.Icon && <subtypeMeta.Icon size={16} />}
              <span
                className="font-mono text-[11.5px] font-semibold tracking-wide truncate"
                style={{ color: subtypeMeta.color || "#EDEDED" }}
              >
                {avi.reference}
              </span>
            </div>
            {/* Spec iter164m — Badge de statut : fond teinté léger
                (couleur du sous-type à 14% d'opacité) + texte dans la
                couleur STATUT réelle (déjà correctement dérivée du
                subtype via AVI_STATUS_OVERRIDES plus haut).
                Spec iter164o (2026-02-25) — Pastille 8px devant l'icône
                pour matcher le pattern KPI/Kanban. */}
            <span
              className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-full px-2 py-0.5"
              style={{
                color: statusMeta.color || "#c8c8cc",
                background: `${subtypeMeta.color || "#2A2A2A"}22`,
                border: `1px solid ${subtypeMeta.color || "#2A2A2A"}44`,
              }}
            >
              <span
                aria-hidden="true"
                className="inline-block rounded-full flex-shrink-0"
                style={{
                  width: 8, height: 8,
                  background: statusMeta.color || "#c8c8cc",
                  boxShadow: `0 0 0 2px ${statusMeta.color || "#c8c8cc"}22`,
                }}
              />
              <StatusIcon size={13} strokeWidth={2.2} />
              {statusMeta.label || avi.status}
            </span>
          </div>

          {/* Objet */}
          <div className="px-3.5 pt-3">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Objet</div>
            <div className="text-[13px] font-semibold text-white leading-snug" data-testid="avi-hovercard-subject">
              {avi.subject}
            </div>
          </div>

          {/* Description */}
          {avi.description && (
            <div className="px-3.5 pt-2.5">
              <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Description</div>
              <div
                className="text-[11.5px] leading-relaxed whitespace-pre-wrap"
                style={{ color: "#C8C8CC", maxHeight: 84, overflow: "hidden",
                         maskImage: "linear-gradient(to bottom, #000 70%, transparent)" }}
                data-testid="avi-hovercard-description"
              >
                {avi.description}
              </div>
            </div>
          )}

          {/* Décideurs / Réceptionnaires */}
          <div className="px-3.5 pt-3">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1.5">
              {activeLabel} · {active.length}
            </div>
            <div className="space-y-1" data-testid="avi-hovercard-active-list">
              {active.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic">Aucun {activeLabel.toLowerCase().slice(0, -1)}.</div>
              )}
              {active.map((p) => {
                const u = usersById[p.user_id];
                const dm = getDecisionMeta(p);
                const DIcon = dm.Icon;
                // Spec iter171 pt.5 (2026-02-27) — Info hovercard élargi :
                // afficher la date de lecture explicite quand disponible.
                let dateLu = null;
                if (p.decision === "lue" && p.decision_at) {
                  try {
                    const d = new Date(p.decision_at);
                    dateLu = `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })} · ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
                  } catch { /* ignore */ }
                }
                return (
                  <div key={p.user_id} className="flex items-center gap-2">
                    <Chip user={u} size={20} />
                    <span className="flex-1 text-[11.5px] truncate" style={{ color: "#E5E5E7" }}>
                      {u ? (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.first_name || u.last_name || "—") : "—"}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold whitespace-nowrap"
                      style={{ color: dm.color }}
                      data-testid={`avi-hovercard-decision-${p.user_id}`}
                    >
                      <DIcon size={11} strokeWidth={2.4} />
                      {dateLu || dm.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Spec iter171 pt.5 (2026-02-27) — Chronologie compacte :
              Créé le, Deadline, Rappel auto (Info), Pièces jointes.
              Aligne le niveau de détail sur le popover ⓘ Documents. */}
          <div className="px-3.5 pt-3">
            <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Chronologie
            </div>
            <ul className="space-y-1 text-[11px]" style={{ color: "#C8C8CC" }}>
              {avi.created_at && (() => {
                try {
                  const d = new Date(avi.created_at);
                  return (
                    <li className="flex items-center gap-1.5">
                      <span style={{ color: "#6B7280" }}>Créée le</span>
                      <span className="tabular-nums" style={{ color: "#EDEDED" }}>
                        {d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} · {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </li>
                  );
                } catch { return null; }
              })()}
              {avi.deadline && (
                <li className="flex items-center gap-1.5">
                  <span style={{ color: "#6B7280" }}>Deadline</span>
                  <span className="tabular-nums" style={{ color: "#F9BC26" }}>{avi.deadline}</span>
                </li>
              )}
              {avi.avi_type === "information" && avi.info_reminder_enabled && (
                <li className="flex items-center gap-1.5">
                  <span style={{ color: "#6B7280" }}>Rappel auto</span>
                  <span style={{ color: "#5aa8e0" }}>
                    si non lu après {avi.info_reminder_days || 0} j{avi.info_reminder_days > 1 ? "" : ""}
                    {avi.info_reminder_last_sent_at ? " · dernier envoi " + new Date(avi.info_reminder_last_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : ""}
                  </span>
                </li>
              )}
              {(avi.attachments || []).length > 0 && (
                <li className="flex items-center gap-1.5">
                  <span style={{ color: "#6B7280" }}>Pièces jointes</span>
                  <span style={{ color: "#EDEDED" }}>
                    {avi.attachments.length} fichier{avi.attachments.length > 1 ? "s" : ""}
                    <span className="text-muted-foreground ml-1 truncate inline-block max-w-[220px] align-middle">
                      · {avi.attachments.slice(0, 3).map((a) => a.filename).join(", ")}
                      {avi.attachments.length > 3 ? "…" : ""}
                    </span>
                  </span>
                </li>
              )}
              {avi.messages_total > 0 && (
                <li className="flex items-center gap-1.5">
                  <span style={{ color: "#6B7280" }}>Discussion</span>
                  <span style={{ color: "#EDEDED" }}>
                    {avi.messages_total} message{avi.messages_total > 1 ? "s" : ""}
                    {avi.messages_unread > 0 && <span className="ml-1" style={{ color: "#EC4899" }}>· {avi.messages_unread} non lu{avi.messages_unread > 1 ? "s" : ""}</span>}
                  </span>
                </li>
              )}
            </ul>
          </div>

          {/* Témoins */}
          {witnesses.length > 0 && (
            <div className="px-3.5 pt-3 pb-3.5">
              <div className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Témoins · {witnesses.length}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {witnesses.map((p) => {
                  const u = usersById[p.user_id];
                  return (
                    <span
                      key={p.user_id}
                      className="inline-flex items-center gap-1.5 rounded-full pl-0.5 pr-2 py-0.5"
                      style={{ background: "#1A1A1A", border: "1px solid #2A2A2A" }}
                    >
                      <Chip user={u} size={16} />
                      <span className="text-[10.5px]" style={{ color: "#C8C8CC" }}>
                        {u ? (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.first_name || u.last_name || "—") : "—"}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin-priority footer */}
          {avi.admin_priority_by && (
            <div
              className="px-3.5 py-2 text-[10.5px] flex items-center gap-1.5"
              style={{ background: "rgba(91,95,199,0.10)", borderTop: "1px solid rgba(91,95,199,0.30)", color: "#93C5FD" }}
              data-testid="avi-hovercard-admin-priority"
            >
              🛡️ Décision tranchée par un Administrateur — les autres réponses ont été bypassées.
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
